import React, { ReactNode, useState } from 'react';
import { useTheme } from '../context/ThemeContext';

interface MobileFrameProps {
  children: ReactNode;
  width?: number;
  className?: string;
  glowColor?: string;
  borderColor?: string;
  notchStyle?: 'pill' | 'dynamic' | 'none';
}

// Detect real mobile devices
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const MobileFrame: React.FC<MobileFrameProps> = ({
  children,
  width = 400,
  className = '',
  glowColor,
  borderColor,
  notchStyle = 'dynamic',
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { theme } = useTheme();

  // ⛔ If real mobile, render directly
  if (isMobileDevice()) {
    return <>{children}</>;
  }

  // Set colors based on theme
  const actualGlowColor = glowColor || (theme === 'dark' ? '#4B5563' : '#6b7280');
  const actualBorderColor = borderColor || (theme === 'dark' ? '#374151' : '#d1d5db');

  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    setIsScrolled(scrollTop > 5);
  };

  const renderNotch = () => {
    switch (notchStyle) {
      case 'pill':
        return (
          <div className="absolute z-10 h-5 transform -translate-x-1/2 bg-black rounded-full top-3 left-1/2 w-36" />
        );
      case 'dynamic':
        return (
          <div className="absolute top-0 flex items-center justify-center transform -translate-x-1/2 left-1/2">
            <div className="flex items-center justify-center w-48 gap-2 px-6 bg-black h-7 rounded-b-3xl">
              <div className="w-2 h-2 bg-gray-600 rounded-full" />
              <div className="w-12 h-2 bg-gray-600 rounded-full" />
              <div className="w-2 h-2 bg-gray-600 rounded-full" />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex items-center justify-center w-full h-screen p-6 transition-colors bg-gradient-to-br from-gray-50 to-gray-200 dark:from-gray-800 dark:to-gray-900">
      <div
        id="mobile-frame"
        className={`overflow-hidden rounded-3xl shadow-lg border-2 backdrop-blur-lg relative ${className}`}
        style={{
          width: '100%',
          maxWidth: `${width}px`,
          minWidth: '340px',
          height: '98vh',
          maxHeight: '750px',
          position: 'relative',
          backgroundColor: theme === 'dark' ? '#121212' : '#ffffff',
          boxShadow: `0 0 60px ${hexToRgba(actualGlowColor, 0.5)}, 0 0 20px ${hexToRgba(actualGlowColor, 0.3)}, inset 0 0 20px rgba(0,0,0,0.03)`,
          borderColor: actualBorderColor,
          borderWidth: '2px',
        }}
      >
        {/* Notch */}
        {/* {renderNotch()} */}

        {/* Content */}
        <div 
          className="w-full h-[calc(100%-28px)] overflow-auto p-5 relative"
          onScroll={handleScroll}
        >
          <div className={`absolute top-0 left-0 right-0 h-6 bg-gradient-to-b ${theme === 'dark' ? 'from-gray-900' : 'from-gray-100'} to-transparent z-10 opacity-0 transition-opacity duration-300 pointer-events-none ${isScrolled ? 'opacity-30' : ''}`} />
          {children}
        </div>

        {/* Bottom gradient */}
        <div className={`absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t ${theme === 'dark' ? 'from-gray-900 via-gray-900' : 'from-white via-white'} to-transparent z-10 pointer-events-none`} />

        {/* Home indicator bar */}
        <div className="absolute z-20 w-32 h-1 transform -translate-x-1/2 bg-gray-300 rounded-full bottom-2 left-1/2 dark:bg-gray-600 opacity-70" />
      </div>
    </div>
  );
};

export default MobileFrame;
